import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Clock, DollarSign, User, X, Search, Users, Layers, Plus, Minus, AlertTriangle } from 'lucide-react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { pets as petsApi, services as servicesApi, groomers as groomersApi, appointments as appointmentsApi, admin as adminApi } from '../utils/api';
import { format, addDays } from 'date-fns';
import { formatTo12Hour } from '../utils/timeFormat';
import AddOnsSelector from '../components/AddOnsSelector';
import useSubjectSettings from '../hooks/useSubjectSettings';
import usePaymentSettings from '../hooks/usePaymentSettings';
import { useAuth } from '../context/AuthContext';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import StripeCheckoutForm from '../components/StripeCheckoutForm';
import SquareCheckoutForm from '../components/SquareCheckoutForm';
import axios from 'axios';
import { track as trackAnalytics } from '../utils/analyticsTracker';
import SEOHead from '../components/SEOHead';
import JsonLd from '../components/JsonLd';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const BookAppointment = () => {
  const navigate = useNavigate();
  const { user, isAdmin, isGroomer } = useAuth();
  const isStaff = isAdmin || isGroomer;
  const { isEnabled: subjectsEnabled, isRequired: subjectRequired, singularName: subjectSingularName, loading: settingsLoading } = useSubjectSettings();
  const {
    canProcessPayments,
    isPaymentRequired,
    allowsPartialPayment, // alias for allowsDownPayment, kept for legacy
    depositPercentage,    // alias for downPaymentPercentage, kept for legacy
    downPaymentType,
    downPaymentFixedAmount,
    computeDownPaymentAmount,
    downPaymentNotice,
    allowsInstallments,
    installmentsMaxCount,
    providers,
  } = usePaymentSettings();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  
  // Client selection for admin/employee booking
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [loadingClients, setLoadingClients] = useState(false);
  const [staffHasPets, setStaffHasPets] = useState(false);
  const [paymentWorkflow, setPaymentWorkflow] = useState('pay_first');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [stripePromise, setStripePromise] = useState(null);
  const [stripeClientSecret, setStripeClientSecret] = useState(null);
  const [squareApplicationId, setSquareApplicationId] = useState(null);
  const [squareLocationId, setSquareLocationId] = useState(null);
  const [showSquareForm, setShowSquareForm] = useState(false);
  const [sizingEnabled, setSizingEnabled] = useState(true);
  const [allowSizeOverride, setAllowSizeOverride] = useState(false);
  const [sizeCategories, setSizeCategories] = useState([]);
  const [selectedSize, setSelectedSize] = useState(null);
  const [servicesEnabled, setServicesEnabled] = useState(true);
  const [addonsEnabled, setAddonsEnabled] = useState(true);
  const [flatRatePrice, setFlatRatePrice] = useState(0);
  const [allowEmployeeSelection, setAllowEmployeeSelection] = useState(true);
  const [serviceImages, setServiceImages] = useState({
    enabled: false,
    position: 'right',
    left: '',
    right: ''
  });
  const [addonImages, setAddonImages] = useState({
    enabled: false,
    position: 'right',
    left: '',
    right: ''
  });
  const hasCheckedPets = useRef(false);
  
  // Payment states. The internal value 'deposit' is kept as-is for backend
  // compatibility (existing rows / payment logs reference this string), but
  // all client-facing copy renders it as "down payment" per terminology rules.
  // 'installments' = client builds a multi-chunk plan; first chunk's method
  // determines whether anything is paid at booking (online → paid via existing
  // provider flow; in_person → nothing paid now, plan stored only).
  const [paymentOption, setPaymentOption] = useState('full'); // 'full', 'deposit', 'installments', or 'later'

  // Installment plan state. Built by the client in the payment step when
  // they pick "Pay in Installments". Each chunk: { amount, method }.
  // method = 'online' (paid via Stripe/PayPal/Square) or 'in_person' (cash
  // at appointment). Chunk 1 is implicitly the "now" chunk — if its method
  // is 'online' it's paid at booking via the existing online-payment flow.
  // Subsequent online chunks sit pending until Phase 2b (link generation).
  const [installmentPlan, setInstallmentPlan] = useState({
    chunks: [{ amount: 0, method: 'online' }, { amount: 0, method: 'in_person' }],
  });
  
  // Helper to get step numbers based on enabled features
  const getStepNumber = (stepName) => {
    let stepNum = 1;
    // For staff, client selection is always step 1
    if (stepName === 'client') return isStaff ? stepNum : null;
    if (isStaff) stepNum++;
    if (stepName === 'pet') return subjectsEnabled ? stepNum : null;
    if (subjectsEnabled) stepNum++;
    if (stepName === 'service') return servicesEnabled ? stepNum : null;
    if (servicesEnabled) stepNum++;
    if (stepName === 'groomer') return stepNum;
    stepNum++;
    if (stepName === 'datetime') return stepNum;
    stepNum++;
    if (stepName === 'addons') return addonsEnabled ? stepNum : null;
    if (addonsEnabled) stepNum++;
    if (stepName === 'payment') return canProcessPayments ? stepNum : null;
    if (canProcessPayments) stepNum++;
    if (stepName === 'confirm') return stepNum;
    return stepNum;
  };

  // Update step when settings load
  useEffect(() => {
    if (!settingsLoading) {
      // For staff, always start at client selection (step 1)
      if (isStaff) {
        setStep(1);
        loadClients();
      } else if (!subjectsEnabled && !servicesEnabled) {
        setStep(getStepNumber('groomer'));
      } else if (!subjectsEnabled) {
        setStep(getStepNumber('service'));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsLoading, subjectsEnabled, servicesEnabled, isStaff]);
  
  // Load clients for staff booking
  const loadClients = async () => {
    setLoadingClients(true);
    try {
      const [clientsRes, myPetsRes] = await Promise.all([
        adminApi.getClients(),
        petsApi.getAll().catch(() => ({ data: [] })),
      ]);
      setClients(clientsRes.data);
      setStaffHasPets(Array.isArray(myPetsRes.data) && myPetsRes.data.length > 0);
    } catch (error) {
      console.error('Load clients error:', error);
      toast.error('Failed to load clients');
    } finally {
      setLoadingClients(false);
    }
  };

  // Handle staff booking for themselves
  const handleBookForMyself = async () => {
    setSelectedClient({ id: user.id, first_name: user.first_name, last_name: user.last_name, email: user.email, isSelf: true });
    try {
      const response = await petsApi.getAll();
      setPets(response.data || []);
    } catch (error) {
      console.error('Load own pets error:', error);
      setPets([]);
    }
    if (subjectsEnabled) {
      setStep(getStepNumber('pet'));
    } else if (servicesEnabled) {
      setStep(getStepNumber('service'));
    } else {
      setStep(getStepNumber('groomer'));
    }
  };
  
  // Handle client selection
  const handleClientSelect = async (client) => {
    setSelectedClient(client);
    // Load pets for the selected client
    try {
      const response = await adminApi.getClientDetails(client.id);
      setPets(response.data.pets || []);
    } catch (error) {
      console.error('Load client pets error:', error);
      setPets([]);
    }
    // Move to next step (pet selection or service if subjects disabled)
    if (subjectsEnabled) {
      setStep(getStepNumber('pet'));
    } else if (servicesEnabled) {
      setStep(getStepNumber('service'));
    } else {
      setStep(getStepNumber('groomer'));
    }
  };
  
  // Filter clients by search term
  const filteredClients = clients.filter(client => {
    if (!clientSearchTerm.trim()) return true;
    const term = clientSearchTerm.toLowerCase();
    return (
      client.first_name?.toLowerCase().includes(term) ||
      client.last_name?.toLowerCase().includes(term) ||
      client.email?.toLowerCase().includes(term)
    );
  });
  
  // Data states
  const [pets, setPets] = useState([]);
  const [services, setServices] = useState([]);
  const [groomers, setGroomers] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [shopClosures, setShopClosures] = useState([]);
  const [businessHours, setBusinessHours] = useState([]);
  
  // Form states
  const [selectedPet, setSelectedPet] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedGroomer, setSelectedGroomer] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [specialRequests, setSpecialRequests] = useState('');
  const [pricing, setPricing] = useState(null);
  const [servicesPage, setServicesPage] = useState(0);
  const ITEMS_PER_PAGE = 5;

  useEffect(() => {
    // Only load data after settings have finished loading
    if (!settingsLoading) {
      loadInitialData();
      loadSizingSettings();
      loadBookingFeatures();
      loadAppointmentSettings();
      loadServiceImages();
      loadPaymentWorkflow();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsLoading]);


  const loadPaymentWorkflow = async () => {
    try {
      const response = await axios.get(`${API_URL}/business-info/payment_workflow`);
      setPaymentWorkflow(response.data.info_value || 'pay_first');
    } catch (error) {
      console.log('Payment workflow not set, defaulting to pay_first');
      setPaymentWorkflow('pay_first');
    }
  };

  const loadSizingSettings = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.get(`${API_URL}/size-categories/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSizingEnabled(response.data.sizing_enabled === 'true');
      setAllowSizeOverride(response.data.allow_size_override === 'true');
      
      // Load size categories if sizing is enabled
      if (response.data.sizing_enabled === 'true') {
        const categoriesRes = await axios.get(`${API_URL}/size-categories`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSizeCategories(categoriesRes.data.filter(c => c.is_active));
      }
    } catch (error) {
      console.error('Failed to load sizing settings:', error);
    }
  };

  const loadBookingFeatures = async () => {
    try {
      const [servicesRes, addonsRes, flatRateRes] = await Promise.all([
        axios.get(`${API_URL}/business-info/services_enabled`).catch(() => ({ data: { info_value: 'true' } })),
        axios.get(`${API_URL}/business-info/addons_enabled`).catch(() => ({ data: { info_value: 'true' } })),
        axios.get(`${API_URL}/business-info/flat_rate_price`).catch(() => ({ data: { info_value: '0' } })),
      ]);
      setServicesEnabled(servicesRes.data.info_value !== 'false');
      setAddonsEnabled(addonsRes.data.info_value !== 'false');
      setFlatRatePrice(parseFloat(flatRateRes.data.info_value) || 0);
    } catch (error) {
      console.error('Failed to load booking features:', error);
    }
  };

  const loadAppointmentSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/business-info/allow_employee_selection`).catch(() => ({ data: { info_value: 'true' } }));
      setAllowEmployeeSelection(response.data.info_value !== 'false');
    } catch (error) {
      console.error('Failed to load appointment settings:', error);
    }
  };

  const loadServiceImages = async () => {
    try {
      const [servicesEnabledRes, servicesPositionRes, servicesLeftRes, servicesRightRes,
             addonsEnabledRes, addonsPositionRes, addonsLeftRes, addonsRightRes] = await Promise.all([
        axios.get(`${API_URL}/business-info/services_image_enabled`).catch(() => ({ data: { info_value: 'false' } })),
        axios.get(`${API_URL}/business-info/services_image_position`).catch(() => ({ data: { info_value: 'right' } })),
        axios.get(`${API_URL}/business-info/services_image_left`).catch(() => ({ data: { info_value: '' } })),
        axios.get(`${API_URL}/business-info/services_image_right`).catch(() => ({ data: { info_value: '' } })),
        axios.get(`${API_URL}/business-info/addons_image_enabled`).catch(() => ({ data: { info_value: 'false' } })),
        axios.get(`${API_URL}/business-info/addons_image_position`).catch(() => ({ data: { info_value: 'right' } })),
        axios.get(`${API_URL}/business-info/addons_image_left`).catch(() => ({ data: { info_value: '' } })),
        axios.get(`${API_URL}/business-info/addons_image_right`).catch(() => ({ data: { info_value: '' } })),
      ]);

      setServiceImages({
        enabled: servicesEnabledRes.data.info_value === 'true',
        position: servicesPositionRes.data.info_value,
        left: servicesLeftRes.data.info_value,
        right: servicesRightRes.data.info_value
      });

      setAddonImages({
        enabled: addonsEnabledRes.data.info_value === 'true',
        position: addonsPositionRes.data.info_value,
        left: addonsLeftRes.data.info_value,
        right: addonsRightRes.data.info_value
      });
    } catch (error) {
      console.error('Failed to load service images:', error);
    }
  };

  const loadInitialData = async () => {
    const token = sessionStorage.getItem('token');

    const [petsResult, servicesResult, groomersResult, closuresResult, hoursResult] = await Promise.allSettled([
      isStaff ? Promise.resolve({ data: [] }) : petsApi.getAll(),
      servicesApi.getAll(),
      groomersApi.getAll(),
      axios.get(`${API_URL}/shop-closures`),
      axios.get(`${API_URL}/business-hours`),
    ]);

    const pets     = petsResult.status     === 'fulfilled' ? petsResult.value.data     : [];
    const services = servicesResult.status === 'fulfilled' ? servicesResult.value.data : [];
    const groomers = groomersResult.status === 'fulfilled' ? groomersResult.value.data : [];
    const closures = closuresResult.status === 'fulfilled' ? closuresResult.value.data : [];
    const hours    = hoursResult.status    === 'fulfilled' ? hoursResult.value.data    : [];

    setPets(pets);
    setServices(services);
    setGroomers([{ id: null, first_name: 'No', last_name: 'Preference' }, ...groomers]);
    setShopClosures(closures);
    setBusinessHours(hours);

    if (servicesResult.status === 'rejected') {
      toast.error('Failed to load booking data');
    }

    // Only check for pets if subjects are enabled AND required, and NOT staff
    // Staff book for clients, so their own pet profile is irrelevant
    if (!isStaff && subjectsEnabled && subjectRequired && pets.length === 0 && !hasCheckedPets.current) {
      hasCheckedPets.current = true;
      toast.info(`Please add a ${subjectSingularName.toLowerCase()} first`);
      navigate('/profile');
    }
  };

  const getSizeFromWeight = (weight, categories) => {
    if (!weight || !categories.length) return null;
    const w = parseFloat(weight);
    if (isNaN(w)) return null;
    for (const cat of categories) {
      const min = cat.min_weight != null ? parseFloat(cat.min_weight) : -Infinity;
      const max = cat.max_weight != null ? parseFloat(cat.max_weight) : Infinity;
      if (w >= min && w <= max) return cat.category_name;
    }
    return categories[categories.length - 1]?.category_name || null;
  };

  const handlePetSelect = (pet) => {
    setSelectedPet(pet);
    if (sizingEnabled) {
      const autoSize = pet.weight ? getSizeFromWeight(pet.weight, sizeCategories) : null;
      setSelectedSize(autoSize || pet.size || null);
    }
    setStep(servicesEnabled ? getStepNumber('service') : getStepNumber('groomer'));
  };

  const handleServiceSelect = (service) => {
    setSelectedService(service);
    // Analytics: service_view fires once per service the client lands on.
    // De-duped client-side within a 1.5s window so re-clicks don't spam.
    trackAnalytics({ path: '/book', eventType: 'service_view', serviceId: service?.id });
    // Use selectedSize if override is allowed, otherwise use pet's stored size
    const petSize = (sizingEnabled && (selectedSize || selectedPet?.size)) 
      ? (selectedSize || selectedPet?.size) 
      : 'standard';
    const servicePricing = service.pricing.find(p => p.size === petSize) || service.pricing[0];
    setPricing(servicePricing);
    setStep(getStepNumber('groomer'));
  };

  const handleGroomerSelect = (groomer) => {
    setSelectedGroomer(groomer);
    setStep(getStepNumber('datetime'));
  };

  const handleDateSelect = async (date) => {
    setSelectedDate(date);
    setSelectedSlot(null);
    setLoading(true);

    try {
      const params = {
        date: format(date, 'yyyy-MM-dd'),
        groomerId: selectedGroomer?.id || undefined,
      };
      
      // Only include serviceId if services are enabled and a service is selected
      if (servicesEnabled && selectedService) {
        params.serviceId = selectedService.id;
        params.petSize = (sizingEnabled && (selectedSize || selectedPet?.size)) ? (selectedSize || selectedPet?.size) : 'standard';
      }
      
      const response = await appointmentsApi.getAvailableSlots(params);
      
      setAvailableSlots(response.data);
      
      if (response.data.length === 0) {
        toast.warning('No available slots for this date');
      }
    } catch (error) {
      toast.error('Failed to load available slots');
    } finally {
      setLoading(false);
    }
  };

  const handleSlotSelect = (slot) => {
    setSelectedSlot(slot);
    setStep(addonsEnabled ? getStepNumber('addons') : (canProcessPayments ? getStepNumber('payment') : getStepNumber('confirm')));
  };

  const getTotalPrice = () => {
    const basePrice = servicesEnabled && pricing ? parseFloat(pricing.price) || 0 : flatRatePrice;
    const addonsPrice = selectedAddons.reduce((sum, addon) => {
      return sum + (addon.price * (addon.quantity || 1));
    }, 0);
    return basePrice + addonsPrice;
  };

  const getPaymentAmount = () => {
    const total = getTotalPrice();
    if (paymentOption === 'deposit' && allowsPartialPayment) {
      // Honours both type='percent' and type='fixed' configurations.
      return computeDownPaymentAmount(total).toFixed(2);
    }
    if (paymentOption === 'installments') {
      // Only the first chunk is paid at booking, and only if its method is
      // 'online'. If the first chunk is 'in_person', nothing is paid now.
      const first = installmentPlan.chunks[0];
      if (first && first.method === 'online') {
        return parseFloat(first.amount || 0).toFixed(2);
      }
      return '0.00';
    }
    return total.toFixed(2);
  };

  // ─── Installment plan helpers ─────────────────────────────────────────

  // Distribute the total evenly across N chunks. Last chunk absorbs any
  // rounding remainder so the sum exactly matches total.
  const buildEvenSplit = (n, total) => {
    if (n < 2) n = 2;
    const per = Math.floor((total / n) * 100) / 100;
    const chunks = Array.from({ length: n }, (_, i) => ({
      amount: per,
      method: i === 0 ? 'online' : 'in_person',
    }));
    const used = +(per * n).toFixed(2);
    const remainder = +(total - used).toFixed(2);
    if (remainder !== 0 && chunks.length > 0) {
      chunks[chunks.length - 1].amount = +(per + remainder).toFixed(2);
    }
    return chunks;
  };

  const installmentSum = () =>
    installmentPlan.chunks.reduce((sum, c) => sum + (parseFloat(c.amount) || 0), 0);

  const isInstallmentPlanValid = () => {
    const chunks = installmentPlan.chunks;
    if (chunks.length < 2 || chunks.length > installmentsMaxCount) return false;
    if (chunks.some((c) => !(parseFloat(c.amount) > 0))) return false;
    return Math.abs(installmentSum() - getTotalPrice()) <= 0.01;
  };

  // Set N chunks (rebuild plan with even split). Called when the count picker
  // changes or when the total changes while installments is selected.
  const setInstallmentChunkCount = (n) => {
    const total = getTotalPrice();
    setInstallmentPlan({ chunks: buildEvenSplit(n, total) });
  };

  const updateInstallmentChunk = (idx, patch) => {
    setInstallmentPlan((prev) => ({
      chunks: prev.chunks.map((c, i) => (i === idx ? { ...c, ...patch } : c)),
    }));
  };

  // When the user first selects "installments" or the total changes, seed
  // the plan with an even 2-chunk split so they have something to edit.
  useEffect(() => {
    if (paymentOption === 'installments') {
      const total = getTotalPrice();
      if (total > 0 && Math.abs(installmentSum() - total) > 0.01) {
        setInstallmentChunkCount(installmentPlan.chunks.length || 2);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentOption, pricing, selectedAddons, flatRatePrice]);

  const handleSubmit = async () => {
    // Analytics: booking_started — user has clicked Confirm and entered the
    // payment / submission phase. Pairs with booking_completed for funnel
    // abandonment tracking ("how many start but don't finish?").
    trackAnalytics({
      path: '/book',
      eventType: 'booking_started',
      serviceId: selectedService?.id,
    });

    // If user selected "Pay in Person" (later), book with cash payment method
    if (paymentOption === 'later') {
      await createAppointment({
        method: 'cash',
        amount: getTotalPrice(),
        transactionId: null
      });
      return;
    }

    // Installments: validate the plan, then either process the first online
    // chunk through the standard payment modal flow, or (when the first chunk
    // is in_person) just create the appointment + plan with no upfront
    // payment. The installment plan itself is POSTed inside createAppointment.
    if (paymentOption === 'installments') {
      if (!isInstallmentPlanValid()) {
        toast.error('Installment plan is invalid. Each chunk must be > $0 and amounts must sum to the total.');
        return;
      }
      const firstChunk = installmentPlan.chunks[0];
      const firstIsOnline = firstChunk?.method === 'online';
      if (firstIsOnline && paymentWorkflow === 'pay_first' && canProcessPayments && providers.filter(p => p.provider !== 'cash_only').length > 0) {
        // Show payment modal — the user picks Stripe/Square/PayPal for the
        // first chunk amount (getPaymentAmount returns the first-chunk amount
        // when paymentOption === 'installments').
        setShowPaymentModal(true);
        return;
      }
      // First chunk is in_person, OR online but no online providers available,
      // OR approve_first workflow. Create the appointment and plan now; no
      // payment is collected at booking. The first chunk will be paid at the
      // appointment (in_person) or via a link sent later (online + approve_first).
      await createAppointment({
        method: 'cash',
        amount: 0,
        transactionId: null,
      });
      return;
    }

    // Check if we need to collect payment first
    if (paymentWorkflow === 'pay_first' && canProcessPayments && providers.filter(p => p.provider !== 'cash_only').length > 0) {
      // Show payment modal for online payment
      setShowPaymentModal(true);
      return;
    }

    // Otherwise, create appointment without payment (approve_first workflow or no online payment)
    await createAppointment();
  };

  const createAppointment = async (paymentData = null) => {
    setLoading(true);

    try {
      const appointmentData = {
        petId: selectedPet?.id || null,
        serviceId: selectedService?.id || null,
        groomerId: selectedGroomer?.id || null,
        appointmentDate: format(selectedDate, 'yyyy-MM-dd'),
        startTime: selectedSlot.startTime,
        specialRequests,
        petSize: selectedSize || null,
        addons: selectedAddons.map(addon => ({
          addonId: addon.id,
          quantity: addon.quantity || 1,
          price: addon.price
        })),
        // For staff booking on behalf of a client
        clientId: isStaff && selectedClient ? selectedClient.id : null
      };

      // Add payment information if payment was processed
      if (paymentData) {
        // For cash payments, status is pending until paid in person
        appointmentData.paymentStatus = paymentData.method === 'cash' ? 'pending' : 'paid';
        appointmentData.paymentMethod = paymentData.method;
        appointmentData.amountPaid = paymentData.method === 'cash' ? 0 : paymentData.amount;
        appointmentData.paymentTransactionId = paymentData.transactionId;
      }

      const createdRes = await appointmentsApi.create(appointmentData);
      const newAppointmentId = createdRes?.data?.id || createdRes?.data?.appointment?.id || null;

      // If the booked service has attached intake forms, the backend
      // spawns one form_submission per form and returns them in
      // pending_forms. Persist the list so AppointmentsEnhanced can show
      // a one-click-away banner with the share links; survives both
      // direct navigation and PayPal/Square redirect flows.
      const pendingForms = createdRes?.data?.pending_forms || [];
      if (pendingForms.length > 0) {
        try {
          sessionStorage.setItem('pendingFormsPostBooking', JSON.stringify(pendingForms));
        } catch (_) { /* storage quota / private mode — ignore */ }
      }

      // Analytics: booking_completed. Best-effort, never blocks UX.
      trackAnalytics({
        path: '/book',
        eventType: 'booking_completed',
        serviceId: selectedService?.id,
      });

      // If this booking is using installments, persist the plan to the backend
      // and (when the first chunk was paid online at booking) immediately mark
      // chunk 1 as paid so the appointment's amount_paid is consistent. The
      // backend recomputes payment_status from the chunks via the route.
      if (paymentOption === 'installments' && newAppointmentId) {
        try {
          const token = sessionStorage.getItem('token');
          await axios.post(
            `${API_URL}/appointments/${newAppointmentId}/installments`,
            { installments: installmentPlan.chunks.map((c) => ({
                amount: parseFloat(c.amount) || 0,
                method: c.method,
              })),
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          // If the first chunk was paid online at booking, mark it paid.
          const firstChunk = installmentPlan.chunks[0];
          const paidOnline = paymentData && paymentData.method && paymentData.method !== 'cash' && firstChunk?.method === 'online';
          if (paidOnline) {
            await axios.patch(
              `${API_URL}/appointments/${newAppointmentId}/installments/1/pay`,
              { payment_id: paymentData.transactionId, payment_method_used: paymentData.method },
              { headers: { Authorization: `Bearer ${token}` } }
            );
          }
        } catch (planErr) {
          // The appointment was created; the plan failed. Surface the error
          // without losing the booking — admin can recreate the plan from the
          // appointment view (Phase 2b admin UI).
          console.error('Failed to persist installment plan:', planErr);
          toast.error('Appointment booked, but the installment plan failed to save. Contact support.');
        }
      }

      setShowPaymentModal(false);

      if (paymentOption === 'installments') {
        const firstChunk = installmentPlan.chunks[0];
        if (paymentData && firstChunk?.method === 'online' && paymentData.method !== 'cash') {
          toast.success('First installment paid! The remaining chunks are scheduled.');
        } else {
          toast.success('Appointment booked! Your installment plan is saved.');
        }
      } else if (paymentWorkflow === 'pay_first') {
        toast.success('Payment successful! Your appointment is pending admin approval.');
      } else {
        toast.success('Appointment request submitted! You will receive a payment link once approved.');
      }
      
      navigate('/appointments');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to book appointment');
    } finally {
      setLoading(false);
    }
  };

  const handlePayPalPayment = async () => {
    setProcessingPayment(true);
    try {
      const token = sessionStorage.getItem('token');
      
      // Create PayPal order for the payable amount (full / down-payment /
      // first-installment depending on paymentOption).
      const createResponse = await axios.post(
        `${API_URL}/payments/create-paypal-order`,
        { amount: parseFloat(getPaymentAmount()) },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const orderId = createResponse.data.orderId;
      const approvalUrl = createResponse.data.approvalUrl;

      if (!approvalUrl) {
        throw new Error('PayPal approval URL not received');
      }

      // Store booking data to complete after PayPal redirect
      sessionStorage.setItem('pendingPayPalBooking', JSON.stringify({
        petId: selectedPet?.id || null,
        serviceId: selectedService?.id || null,
        groomerId: selectedGroomer?.id || null,
        appointmentDate: format(selectedDate, 'yyyy-MM-dd'),
        startTime: selectedSlot.startTime,
        specialRequests,
        petSize: selectedSize || null,
        addons: selectedAddons.map(addon => ({
          addonId: addon.id,
          quantity: addon.quantity || 1,
          price: addon.price
        })),
        orderId: orderId,
        amount: getTotalPrice()
      }));

      // Redirect to PayPal for approval
      toast.info('Redirecting to PayPal...');
      window.location.href = approvalUrl;
    } catch (error) {
      console.error('PayPal payment error:', error);
      toast.error(error.response?.data?.error || 'Payment failed');
      setProcessingPayment(false);
    }
  };

  const handleStripePayment = async () => {
    setProcessingPayment(true);
    try {
      const token = sessionStorage.getItem('token');
      
      // Get Stripe publishable key from providers
      const stripeProvider = providers.find(p => p.provider === 'stripe');
      if (!stripeProvider || !stripeProvider.public_key) {
        toast.error('Stripe is not properly configured');
        setProcessingPayment(false);
        return;
      }

      // Initialize Stripe
      const stripe = await loadStripe(stripeProvider.public_key);
      setStripePromise(stripe);
      
      // Create Stripe payment intent. Use getPaymentAmount() so deposit and
      // installment modes charge the correct partial amount, not the total.
      const response = await axios.post(
        `${API_URL}/payments/create-payment-intent`,
        { amount: parseFloat(getPaymentAmount()) },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setStripeClientSecret(response.data.clientSecret);
      // The form will handle the rest
    } catch (error) {
      console.error('Stripe payment error:', error);
      toast.error(error.response?.data?.error || 'Payment failed');
      setProcessingPayment(false);
    }
  };

  const handleStripeSuccess = async (paymentData) => {
    await createAppointment({
      method: 'stripe',
      // Pay only the down-payment / first-installment amount when applicable.
      amount: parseFloat(getPaymentAmount()),
      transactionId: paymentData.transactionId
    });
  };

  const handleStripeCancel = () => {
    setStripeClientSecret(null);
    setStripePromise(null);
    setProcessingPayment(false);
    setSelectedPaymentMethod(null);
  };

  const handleSquarePayment = async () => {
    setProcessingPayment(true);
    try {
      // Get Square configuration from providers
      const squareProvider = providers.find(p => p.provider === 'square');
      if (!squareProvider || !squareProvider.public_key || !squareProvider.location_id) {
        toast.error('Square is not properly configured');
        setProcessingPayment(false);
        return;
      }

      // Set Square credentials and show form
      setSquareApplicationId(squareProvider.public_key);
      setSquareLocationId(squareProvider.location_id);
      setShowSquareForm(true);
    } catch (error) {
      console.error('Square payment error:', error);
      toast.error(error.response?.data?.error || 'Payment failed');
      setProcessingPayment(false);
    }
  };

  const handleSquareSuccess = async (paymentData) => {
    await createAppointment({
      method: 'square',
      amount: parseFloat(getPaymentAmount()),
      transactionId: paymentData.transactionId
    });
  };

  const handleSquareCancel = () => {
    setShowSquareForm(false);
    setSquareApplicationId(null);
    setSquareLocationId(null);
    setProcessingPayment(false);
    setSelectedPaymentMethod(null);
  };

  const handlePaymentMethodClick = async (method) => {
    setSelectedPaymentMethod(method);
    
    switch (method) {
      case 'paypal':
        await handlePayPalPayment();
        break;
      case 'stripe':
        await handleStripePayment();
        break;
      case 'square':
        await handleSquarePayment();
        break;
      default:
        toast.error('Invalid payment method');
    }
  };

  const minDate = new Date();
  const maxDate = addDays(new Date(), 60);

  // Check if a date is a shop closure
  const isDateClosed = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const month = date.getMonth() + 1;
    const day = date.getDate();

    const isClosed = shopClosures.some(closure => {
      // Normalize the closure date (remove time component if present)
      const closureDate = closure.closure_date.split('T')[0];
      
      // Check exact date match
      if (closureDate === dateStr) {
        console.log('Exact date match:', closureDate, '===', dateStr);
        return true;
      }
      
      // Check recurring closure (same month and day)
      if (closure.is_recurring && 
          closure.recurrence_month === month && 
          closure.recurrence_day === day) {
        console.log('Recurring match:', closure.recurrence_month, '/', closure.recurrence_day, '===', month, '/', day);
        return true;
      }
      
      return false;
    });

    if (isClosed) {
      console.log('Date is closed:', dateStr, shopClosures);
    }

    return isClosed;
  };

  // Get closure reason for a date
  const getClosureReason = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const month = date.getMonth() + 1;
    const day = date.getDate();

    const closure = shopClosures.find(c => {
      const closureDate = c.closure_date.split('T')[0];
      if (closureDate === dateStr) return true;
      if (c.is_recurring && c.recurrence_month === month && c.recurrence_day === day) return true;
      return false;
    });

    return closure?.reason || 'Shop Closed';
  };

  // Check if a day of the week is closed per business hours
  const isDayOfWeekClosed = (date) => {
    if (!businessHours.length) return false;
    const dayOfWeek = date.getDay(); // 0=Sunday, 6=Saturday
    const hoursForDay = businessHours.find(h => h.day_of_week === dayOfWeek);
    return hoursForDay ? !hoursForDay.is_active : false;
  };

  // Disable closed dates on calendar
  const tileDisabled = ({ date, view }) => {
    if (view === 'month') {
      return isDateClosed(date) || isDayOfWeekClosed(date);
    }
    return false;
  };

  // Add custom class to closed dates
  const tileClassName = ({ date, view }) => {
    if (view === 'month' && isDateClosed(date)) {
      return 'shop-closed-date';
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      {/* SEO: booking page is the conversion target. The ReserveAction
          schema may surface as a "Reserve" action chip in supported Google
          verticals. We pass the currently-selected service so structured
          data updates as the client narrows down. */}
      <SEOHead
        title="Book an Appointment"
        description={`Book a ${selectedService?.name || 'service'} appointment online — pick a time that works for you and confirm in seconds.`}
      />
      <JsonLd
        type="BookingAction"
        data={{ serviceId: selectedService?.id, serviceName: selectedService?.name }}
      />

      <div className="max-w-4xl mx-auto px-4">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Book an Appointment</h1>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {(() => {
              const steps = [];
              if (subjectsEnabled) steps.push(subjectSingularName);
              if (servicesEnabled) steps.push('Service');
              steps.push('Employee', 'Date & Time');
              if (addonsEnabled) steps.push('Add-ons');
              if (canProcessPayments) steps.push('Payment');
              steps.push('Confirm');
              return steps;
            })().map((label, index) => {
              const actualStep = index + 1;
              return (
                <div key={label} className="flex items-center">
                  <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
                    step > actualStep ? 'bg-green-500' : step === actualStep ? 'bg-primary-600' : 'bg-gray-300'
                  } text-white font-bold`}>
                    {step > actualStep ? '✓' : actualStep}
                  </div>
                  <span className="ml-2 text-sm font-medium text-gray-700">{label}</span>
                  {index < (() => {
                    let count = 0;
                    if (subjectsEnabled) count++;
                    if (servicesEnabled) count++;
                    count += 2; // Employee + Date & Time
                    if (addonsEnabled) count++;
                    if (canProcessPayments) count++;
                    count++; // Confirm
                    return count - 1;
                  })() && <div className="w-12 h-1 mx-2 bg-gray-300"></div>}
                </div>
              );
            })}
          </div>
        </div>

        {/* Step: Select Client (only for staff) */}
        {isStaff && step === getStepNumber('client') && (
          <div className="card">
            <h2 className="text-2xl font-bold mb-6">
              <Users className="inline-block mr-2" size={28} />
              Select Client
            </h2>

            {/* Book for Myself option - only if staff has pets */}
            {staffHasPets && (
              <div className="mb-6">
                <button
                  onClick={handleBookForMyself}
                  className="w-full p-4 border-2 border-primary-200 bg-primary-50 rounded-lg hover:border-primary-600 transition-colors text-left"
                >
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 rounded-full bg-primary-600 flex items-center justify-center">
                      <User size={24} className="text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-lg text-primary-700">Book for Myself</h3>
                      <p className="text-primary-600 text-sm">Book an appointment for your own pet</p>
                    </div>
                  </div>
                </button>
                <div className="relative my-4">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-3 bg-white text-gray-500">or select a client</span>
                  </div>
                </div>
              </div>
            )}
            
            {/* Search */}
            <div className="relative mb-6">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search clients by name or email..."
                value={clientSearchTerm}
                onChange={(e) => setClientSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            
            {loadingClients ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              </div>
            ) : filteredClients.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                {clientSearchTerm ? 'No clients found matching your search' : 'No clients available'}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-96 overflow-y-auto">
                {filteredClients.map((client) => (
                  <button
                    key={client.id}
                    onClick={() => handleClientSelect(client)}
                    className="p-4 border-2 border-gray-200 rounded-lg hover:border-primary-600 transition-colors text-left"
                  >
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center">
                        <User size={24} className="text-primary-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-lg truncate">
                          {client.first_name} {client.last_name}
                        </h3>
                        <p className="text-gray-600 text-sm truncate">{client.email}</p>
                        {client.phone && <p className="text-gray-500 text-sm">{client.phone}</p>}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Step: Select Pet (only if subjects enabled) */}
        {step === getStepNumber('pet') && (
          <div className="card">
            {isStaff && (
              <button onClick={() => setStep(getStepNumber('client'))} className="mb-4 text-primary-600 hover:text-primary-700">
                ← Back to Client Selection
              </button>
            )}
            <h2 className="text-2xl font-bold mb-6">Select {isStaff && selectedClient ? `${selectedClient.first_name}'s` : 'Your'} {subjectSingularName}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {pets.map((pet) => (
                <button
                  key={pet.id}
                  onClick={() => handlePetSelect(pet)}
                  className="p-4 border-2 border-gray-200 rounded-lg hover:border-primary-600 transition-colors text-left"
                >
                  <div className="flex items-center space-x-4">
                    {pet.image_url ? (
                      <img src={pet.image_url} alt={pet.name} className="w-16 h-16 rounded-full object-cover" />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center text-2xl">
                        🐕
                      </div>
                    )}
                    <div>
                      <h3 className="font-bold text-lg">{pet.name}</h3>
                      <p className="text-gray-600">{pet.breed || 'Mixed Breed'}</p>
                      {pet.weight && <p className="text-sm text-gray-500">{pet.weight} lbs</p>}
                      {pet.age && <p className="text-sm text-gray-500">{pet.age} years old</p>}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step: Select Service */}
        {step === getStepNumber('service') && (
          <div className="card">
            {subjectsEnabled ? (
              <button onClick={() => setStep(getStepNumber('pet'))} className="mb-4 text-primary-600 hover:text-primary-700">
                ← Back
              </button>
            ) : isStaff && (
              <button onClick={() => setStep(getStepNumber('client'))} className="mb-4 text-primary-600 hover:text-primary-700">
                ← Back to Client Selection
              </button>
            )}
            <h2 className="text-2xl font-bold mb-6">Select Service</h2>
            
            <div className="flex gap-6">
              {/* Left Image */}
              {serviceImages.enabled && (serviceImages.position === 'left' || serviceImages.position === 'both') && serviceImages.left && (
                <div className="hidden lg:block w-64 flex-shrink-0">
                  <img 
                    src={serviceImages.left} 
                    alt="Services" 
                    className="w-full h-auto rounded-lg shadow-lg object-cover sticky top-4"
                    style={{ maxHeight: '500px' }}
                  />
                </div>
              )}

              {/* Size Selector - Staff can override; clients see locked display */}
              {sizingEnabled && sizeCategories.length > 0 && (
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Size Category
                  </label>
                  {isStaff ? (
                    <>
                      <select
                        value={selectedSize || ''}
                        onChange={(e) => setSelectedSize(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      >
                        {sizeCategories.map(category => (
                          <option key={category.id} value={category.category_name}>
                            {category.category_label}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">Override size if needed</p>
                    </>
                  ) : (
                    <>
                      <div className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-white text-gray-800 font-medium">
                        {sizeCategories.find(c => c.category_name === selectedSize)?.category_label || selectedSize || 'Not determined'}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Determined by your pet's weight</p>
                    </>
                  )}
                </div>
              )}

              {/* Services List */}
              <div className="flex-1">
                <div className="space-y-4">
                  {services
                    .slice(servicesPage * ITEMS_PER_PAGE, (servicesPage + 1) * ITEMS_PER_PAGE)
                    .map((service) => {
                      // Use selectedSize if available, otherwise pet's stored size
                      const petSize = (sizingEnabled && (selectedSize || selectedPet?.size)) ? (selectedSize || selectedPet?.size) : 'standard';
                      const petPricing = service.pricing.find(p => p.size === petSize) || service.pricing[0];
                      return (
                        <button
                          key={service.id}
                          onClick={() => handleServiceSelect(service)}
                          className="w-full p-6 border-2 border-gray-200 rounded-lg hover:border-primary-600 transition-colors text-left"
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <h3 className="font-bold text-xl mb-2">{service.name}</h3>
                              <p className="text-gray-600 mb-3">{service.description}</p>
                              <div className="flex items-center space-x-4 text-sm text-gray-500">
                                <span className="flex items-center">
                                  <Clock size={16} className="mr-1" />
                                  {petPricing?.durationMinutes} min
                                </span>
                                <span className="flex items-center">
                                  <DollarSign size={16} className="mr-1" />
                                  ${petPricing?.price}
                                </span>
                              </div>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                </div>

                {/* Pagination Controls */}
                {services.length > ITEMS_PER_PAGE && (
                  <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
                    <button
                      onClick={() => setServicesPage(Math.max(0, servicesPage - 1))}
                      disabled={servicesPage === 0}
                      className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span>←</span>
                      <span>Previous</span>
                    </button>
                    
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-600">
                        Page {servicesPage + 1} of {Math.ceil(services.length / ITEMS_PER_PAGE)}
                      </span>
                      <span className="text-xs text-gray-500">
                        ({services.length} total)
                      </span>
                    </div>

                    <button
                      onClick={() => setServicesPage(Math.min(Math.ceil(services.length / ITEMS_PER_PAGE) - 1, servicesPage + 1))}
                      disabled={servicesPage >= Math.ceil(services.length / ITEMS_PER_PAGE) - 1}
                      className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span>Next</span>
                      <span>→</span>
                    </button>
                  </div>
                )}
              </div>

              {/* Right Image */}
              {serviceImages.enabled && (serviceImages.position === 'right' || serviceImages.position === 'both') && serviceImages.right && (
                <div className="hidden lg:block w-64 flex-shrink-0">
                  <img 
                    src={serviceImages.right} 
                    alt="Services" 
                    className="w-full h-auto rounded-lg shadow-lg object-cover sticky top-4"
                    style={{ maxHeight: '500px' }}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step: Select Employee */}
        {step === getStepNumber('groomer') && (
          <div className="card">
            <button onClick={() => setStep(servicesEnabled ? getStepNumber('service') : getStepNumber('pet'))} className="mb-4 text-primary-600 hover:text-primary-700">
              ← Back
            </button>
            <h2 className="text-2xl font-bold mb-6">Select Employee (Optional)</h2>
            
            {allowEmployeeSelection ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {groomers.map((groomer) => (
                  <button
                    key={groomer.id || 'no-pref'}
                    onClick={() => handleGroomerSelect(groomer)}
                    className="p-4 border-2 border-gray-200 rounded-lg hover:border-primary-600 transition-colors text-left"
                  >
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center">
                        <User size={24} className="text-primary-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-bold">{groomer.first_name} {groomer.last_name}</h3>
                        {groomer.bio && <p className="text-sm text-gray-600 mt-1">{groomer.bio}</p>}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
                <p className="text-gray-700 mb-4">
                  Employee selection is currently disabled. Your appointment will be assigned to an available team member.
                </p>
                <button
                  onClick={() => handleGroomerSelect({ id: null })}
                  className="btn btn-primary"
                >
                  Continue to Date & Time
                </button>
              </div>
            )}
          </div>
        )}

        {/* Step: Select Date and Time */}
        {step === getStepNumber('datetime') && (
          <div className="card">
            <button onClick={() => setStep(getStepNumber('groomer'))} className="mb-4 text-primary-600 hover:text-primary-700">
              ← Back
            </button>
            <h2 className="text-2xl font-bold mb-6">Select Date & Time</h2>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <h3 className="font-semibold mb-4">Choose a Date</h3>
                <Calendar
                  onChange={handleDateSelect}
                  value={selectedDate}
                  minDate={minDate}
                  maxDate={maxDate}
                  tileDisabled={tileDisabled}
                  tileClassName={tileClassName}
                  className="w-full border-0 shadow-sm"
                />
                {selectedDate && isDateClosed(selectedDate) && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-800 font-medium">
                      🚫 Shop Closed: {getClosureReason(selectedDate)}
                    </p>
                  </div>
                )}
              </div>

              {selectedDate && (
                <div>
                  <h3 className="font-semibold mb-4">Available Time Slots</h3>
                  {loading ? (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                    </div>
                  ) : availableSlots.length > 0 ? (
                    <div className="grid grid-cols-2 gap-2 max-h-96 overflow-y-auto">
                      {availableSlots.map((slot, index) => (
                        <button
                          key={index}
                          onClick={() => handleSlotSelect(slot)}
                          className="p-3 border-2 border-gray-200 rounded-lg hover:border-primary-600 transition-colors"
                        >
                          <div className="flex items-center justify-center">
                            <Clock size={16} className="mr-2" />
                            <span className="font-medium">{formatTo12Hour(slot.startTime)}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-8">No available slots for this date</p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step: Add-ons */}
        {step === getStepNumber('addons') && (
          <div className="card">
            <button onClick={() => setStep(getStepNumber('datetime'))} className="mb-4 text-primary-600 hover:text-primary-700">
              ← Back
            </button>
            <h2 className="text-2xl font-bold mb-6">Add Popular Extras (Optional)</h2>
            
            <div className="flex gap-6">
              {/* Left Image */}
              {addonImages.enabled && (addonImages.position === 'left' || addonImages.position === 'both') && addonImages.left && (
                <div className="hidden lg:block w-64 flex-shrink-0">
                  <img 
                    src={addonImages.left} 
                    alt="Add-ons" 
                    className="w-full h-auto rounded-lg shadow-lg object-cover sticky top-4"
                    style={{ maxHeight: '500px' }}
                  />
                </div>
              )}

              {/* Add-ons Selector */}
              <div className="flex-1">
                <AddOnsSelector
                  selectedAddons={selectedAddons}
                  onAddonsChange={setSelectedAddons}
                />
              </div>

              {/* Right Image */}
              {addonImages.enabled && (addonImages.position === 'right' || addonImages.position === 'both') && addonImages.right && (
                <div className="hidden lg:block w-64 flex-shrink-0">
                  <img 
                    src={addonImages.right} 
                    alt="Add-ons" 
                    className="w-full h-auto rounded-lg shadow-lg object-cover sticky top-4"
                    style={{ maxHeight: '500px' }}
                  />
                </div>
              )}
            </div>

            <div className="flex space-x-4 mt-6">
              <button
                onClick={() => setStep(canProcessPayments ? getStepNumber('payment') : getStepNumber('confirm'))}
                className="flex-1 btn btn-primary"
              >
                Continue to Review
              </button>
            </div>
          </div>
        )}

        {/* Step: Payment (if enabled) */}
        {step === getStepNumber('payment') && (
          <div className="card">
            <button onClick={() => setStep(addonsEnabled ? getStepNumber('addons') : getStepNumber('datetime'))} className="mb-4 text-primary-600 hover:text-primary-700">
              ← Back
            </button>
            <h2 className="text-2xl font-bold mb-6">Payment Options</h2>
            
            <div className="space-y-6">
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-gray-900">Total Amount</h3>
                  <div className="text-2xl font-bold text-primary-600">
                    ${getTotalPrice().toFixed(2)}
                  </div>
                </div>
                <p className="text-sm text-gray-600">
                  Service: ${pricing?.price.toFixed(2)}
                  {selectedAddons.length > 0 && ` + Add-ons: $${selectedAddons.reduce((sum, a) => sum + (a.price * a.quantity), 0).toFixed(2)}`}
                </p>
              </div>

              {(() => {
                // Compute the active online-provider set once so every payment
                // option below can show consistent inline copy and gate itself
                // on whether online payment is even possible. cash_only is
                // explicitly excluded — it represents "pay in person" and is
                // surfaced as its own radio further down.
                const onlineProviders = providers
                  .filter(p => p.provider !== 'cash_only' && p.is_active)
                  .map(p => p.provider);
                const hasOnlineProviders = onlineProviders.length > 0;
                const providersLabel = onlineProviders.join(', ') || 'online payment';
                return (
                  <div className="space-y-3">
                    <h3 className="font-semibold text-gray-900">Select Payment Option</h3>

                    {/* Pay Full Amount */}
                    <label className="flex items-start p-4 border-2 rounded-lg cursor-pointer hover:border-primary-600 transition-colors">
                      <input
                        type="radio"
                        name="paymentOption"
                        value="full"
                        checked={paymentOption === 'full'}
                        onChange={(e) => setPaymentOption(e.target.value)}
                        className="mt-1 w-4 h-4 text-primary-600"
                      />
                      <div className="ml-3 flex-1">
                        <div className="font-semibold">Pay Full Amount Now</div>
                        <div className="text-sm text-gray-600">
                          Pay ${getTotalPrice().toFixed(2)} via {providersLabel}
                        </div>
                      </div>
                    </label>

                    {/* Pay Down Payment. Requires at least one active online
                        provider — a down payment without an online payment
                        method would silently fall through to a no-payment
                        booking, which is misleading to clients and admins. If
                        the admin has the toggle on but no online providers
                        configured, we surface a small explanatory notice so
                        they know why this option is hidden from clients.
                        Internally still tagged as 'deposit' for backend compat. */}
                    {allowsPartialPayment && hasOnlineProviders && (() => {
                      const total = getTotalPrice();
                      const downAmount = computeDownPaymentAmount(total);
                      const remaining = Math.max(total - downAmount, 0);
                      const headline = downPaymentType === 'fixed'
                        ? `Pay Down Payment ($${downAmount.toFixed(2)})`
                        : `Pay Down Payment (${depositPercentage}% = $${downAmount.toFixed(2)})`;
                      return (
                        <label className="flex items-start p-4 border-2 rounded-lg cursor-pointer hover:border-primary-600 transition-colors">
                          <input
                            type="radio"
                            name="paymentOption"
                            value="deposit"
                            checked={paymentOption === 'deposit'}
                            onChange={(e) => setPaymentOption(e.target.value)}
                            className="mt-1 w-4 h-4 text-primary-600"
                          />
                          <div className="ml-3 flex-1">
                            <div className="font-semibold">{headline}</div>
                            <div className="text-sm text-gray-600">
                              Pay ${downAmount.toFixed(2)} online now via {providersLabel}, remaining ${remaining.toFixed(2)} in cash at appointment
                            </div>
                            {paymentOption === 'deposit' && (
                              <>
                                <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-900">
                                  <strong>Non-refundable:</strong> {downPaymentNotice}
                                </div>
                                {onlineProviders.length > 1 && (
                                  <div className="mt-2 text-xs text-gray-600">
                                    You'll choose which provider ({providersLabel}) on the next step.
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </label>
                      );
                    })()}

                    {/* Admin-facing notice: down payments enabled but no online
                        provider configured. Hidden from regular clients so they
                        don't see internal config gaps; admins/staff browsing
                        the booking flow get a clear explanation of what's
                        missing. */}
                    {allowsPartialPayment && !hasOnlineProviders && (isAdmin || isStaff) && (
                      <div className="p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-900">
                        <strong>Down payments enabled, but no online provider is active.</strong>{' '}
                        Configure Stripe, PayPal, or Square in Payment Settings to start collecting
                        down payments. Until then, clients won't see the down-payment option here.
                      </div>
                    )}

                {/* Pay in Installments (split full total into N chunks) */}
                {allowsInstallments && getTotalPrice() > 0 && (
                  <label className="flex items-start p-4 border-2 rounded-lg cursor-pointer hover:border-primary-600 transition-colors">
                    <input
                      type="radio"
                      name="paymentOption"
                      value="installments"
                      checked={paymentOption === 'installments'}
                      onChange={(e) => setPaymentOption(e.target.value)}
                      className="mt-1 w-4 h-4 text-primary-600"
                    />
                    <div className="ml-3 flex-1">
                      <div className="font-semibold flex items-center">
                        <Layers size={16} className="mr-2 text-sky-600" />
                        Pay in Installments
                      </div>
                      <div className="text-sm text-gray-600">
                        Split ${getTotalPrice().toFixed(2)} into {installmentPlan.chunks.length} chunks. Each chunk can be paid online or in person.
                      </div>

                      {paymentOption === 'installments' && (
                        <div className="mt-3 space-y-3 p-3 bg-sky-50 border border-sky-200 rounded">
                          {/* Chunk count picker */}
                          <div className="flex items-center justify-between">
                            <label className="text-sm font-medium text-gray-700">Number of installments</label>
                            <div className="flex items-center space-x-2">
                              <button
                                type="button"
                                onClick={() => setInstallmentChunkCount(Math.max(2, installmentPlan.chunks.length - 1))}
                                disabled={installmentPlan.chunks.length <= 2}
                                className="p-1 rounded bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                aria-label="Fewer installments"
                              >
                                <Minus size={14} />
                              </button>
                              <span className="font-semibold w-6 text-center">{installmentPlan.chunks.length}</span>
                              <button
                                type="button"
                                onClick={() => setInstallmentChunkCount(Math.min(installmentsMaxCount, installmentPlan.chunks.length + 1))}
                                disabled={installmentPlan.chunks.length >= installmentsMaxCount}
                                className="p-1 rounded bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                aria-label="More installments"
                              >
                                <Plus size={14} />
                              </button>
                              <button
                                type="button"
                                onClick={() => setInstallmentChunkCount(installmentPlan.chunks.length)}
                                className="ml-2 text-xs text-sky-700 hover:text-sky-900 font-medium"
                              >
                                Even split
                              </button>
                            </div>
                          </div>
                          <div className="text-xs text-gray-500">
                            You can choose between 2 and {installmentsMaxCount} installments.
                          </div>

                          {/* Per-chunk rows */}
                          <div className="space-y-2">
                            {installmentPlan.chunks.map((chunk, idx) => (
                              <div key={idx} className="flex items-center space-x-2 bg-white p-2 rounded border border-gray-200">
                                <span className="text-sm font-medium text-gray-700 w-8">#{idx + 1}</span>
                                <div className="relative flex-1">
                                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                                  <input
                                    type="number"
                                    step="0.01"
                                    min="0.01"
                                    value={chunk.amount}
                                    onChange={(e) => updateInstallmentChunk(idx, { amount: e.target.value })}
                                    onClick={(e) => e.stopPropagation()}
                                    className="w-full pl-6 pr-2 py-1 text-sm border border-gray-300 rounded"
                                  />
                                </div>
                                <select
                                  value={chunk.method}
                                  onChange={(e) => updateInstallmentChunk(idx, { method: e.target.value })}
                                  onClick={(e) => e.stopPropagation()}
                                  className="text-sm border border-gray-300 rounded px-2 py-1 bg-white"
                                >
                                  <option value="online">Online</option>
                                  <option value="in_person">In person</option>
                                </select>
                                <span className="text-xs text-gray-500 w-20 text-right">
                                  {idx === 0 && chunk.method === 'online' ? 'Paid now' :
                                   idx === 0 && chunk.method === 'in_person' ? 'At appointment' :
                                   chunk.method === 'in_person' ? 'At appointment' :
                                   'Link later'}
                                </span>
                              </div>
                            ))}
                          </div>

                          {/* Sum validation */}
                          {(() => {
                            const sum = installmentSum();
                            const total = getTotalPrice();
                            const delta = +(sum - total).toFixed(2);
                            const ok = Math.abs(delta) <= 0.01;
                            return (
                              <div className={`flex items-center justify-between text-sm p-2 rounded ${
                                ok ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'
                              }`}>
                                <span>
                                  Sum: <strong>${sum.toFixed(2)}</strong> / Total: <strong>${total.toFixed(2)}</strong>
                                </span>
                                <span>
                                  {ok ? '✓ Plan balanced' : (delta > 0 ? `Over by $${delta.toFixed(2)}` : `Short by $${Math.abs(delta).toFixed(2)}`)}
                                </span>
                              </div>
                            );
                          })()}

                          {/* Notes */}
                          <div className="text-xs text-gray-600 space-y-1">
                            <div className="flex items-start space-x-1">
                              <AlertTriangle size={12} className="flex-shrink-0 mt-0.5 text-amber-600" />
                              <span>
                                Online chunks after the first will receive a payment link before their due date. In-person chunks are paid in cash at the appointment.
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </label>
                )}

                {/* Pay in Person (always show if cash_only is active) */}
                {providers.some(p => p.provider === 'cash_only' && p.is_active) && (
                  <label className="flex items-start p-4 border-2 rounded-lg cursor-pointer hover:border-primary-600 transition-colors">
                    <input
                      type="radio"
                      name="paymentOption"
                      value="later"
                      checked={paymentOption === 'later'}
                      onChange={(e) => setPaymentOption(e.target.value)}
                      className="mt-1 w-4 h-4 text-primary-600"
                    />
                    <div className="ml-3 flex-1">
                      <div className="font-semibold">Pay in Person</div>
                      <div className="text-sm text-gray-600">Pay ${getTotalPrice().toFixed(2)} when you arrive</div>
                    </div>
                  </label>
                )}
                  </div>
                );
              })()}


              {isPaymentRequired && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Note:</strong> Payment is required to complete your booking.
                  </p>
                </div>
              )}
            </div>

            <div className="flex space-x-4 mt-6">
              <button
                onClick={() => setStep(getStepNumber('confirm'))}
                className="flex-1 btn btn-primary"
              >
                Continue to Confirmation
              </button>
            </div>
          </div>
        )}

        {/* Step: Confirm */}
        {step === getStepNumber('confirm') && (
          <div className="card">
            <button onClick={() => setStep(canProcessPayments ? getStepNumber('payment') : (addonsEnabled ? getStepNumber('addons') : getStepNumber('datetime')))} className="mb-4 text-primary-600 hover:text-primary-700">
              ← Back
            </button>
            <h2 className="text-2xl font-bold mb-6">Confirm Appointment</h2>
            
            <div className="space-y-4 mb-6">
              {subjectsEnabled && selectedPet && (
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-semibold mb-2">{subjectSingularName}</h3>
                  <p>{selectedPet?.name} {selectedPet?.breed && `(${selectedPet.breed})`}</p>
                </div>
              )}

              {servicesEnabled && selectedService ? (
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-semibold mb-2">Service</h3>
                  <p>{selectedService.name}</p>
                  <p className="text-sm text-gray-600">{pricing?.durationMinutes} minutes - ${parseFloat(pricing?.price || 0).toFixed(2)}</p>
                </div>
              ) : (
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-semibold mb-2">Appointment</h3>
                  <p>Standard Appointment</p>
                  {flatRatePrice > 0 && <p className="text-sm text-gray-600">Flat Rate: ${flatRatePrice.toFixed(2)}</p>}
                </div>
              )}

              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="font-semibold mb-2">Employee</h3>
                <p>{selectedGroomer?.id ? `${selectedGroomer.first_name} ${selectedGroomer.last_name}` : 'No preference'}</p>
              </div>

              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="font-semibold mb-2">Date & Time</h3>
                <p>{format(selectedDate, 'MMMM d, yyyy')} at {formatTo12Hour(selectedSlot?.startTime)}</p>
              </div>

              {addonsEnabled && selectedAddons.length > 0 && (
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-semibold mb-2">Add-ons</h3>
                  <ul className="space-y-1">
                    {selectedAddons.map(addon => (
                      <li key={addon.id} className="flex justify-between text-sm">
                        <span>{addon.name} {addon.quantity > 1 && `(x${addon.quantity})`}</span>
                        <span className="font-semibold">${(addon.price * addon.quantity).toFixed(2)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="p-4 bg-primary-50 rounded-lg border-2 border-primary-200">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="font-semibold text-gray-900">Total Price</h3>
                    <p className="text-sm text-gray-600">
                      {servicesEnabled && pricing ? `Service: $${parseFloat(pricing.price).toFixed(2)}` : (flatRatePrice > 0 ? `Flat Rate: $${flatRatePrice.toFixed(2)}` : 'Add-ons only')}
                      {selectedAddons.length > 0 && ` + Add-ons: $${selectedAddons.reduce((sum, a) => sum + (a.price * a.quantity), 0).toFixed(2)}`}
                    </p>
                    {canProcessPayments && paymentOption !== 'later' && (
                      <p className="text-sm text-green-700 font-semibold mt-1">
                        {paymentOption === 'full'
                          ? '✓ Paying full amount'
                          : `✓ Paying $${computeDownPaymentAmount(getTotalPrice()).toFixed(2)} down payment (non-refundable)`}
                      </p>
                    )}
                  </div>
                  <div className="text-3xl font-bold text-primary-600">
                    ${getTotalPrice().toFixed(2)}
                  </div>
                </div>
                {canProcessPayments && paymentOption !== 'later' && (
                  <div className="mt-3 pt-3 border-t border-primary-200">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">Amount to pay now:</span>
                      <span className="text-xl font-bold text-green-600">${getPaymentAmount()}</span>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="label">Special Requests (Optional)</label>
                <textarea
                  className="input"
                  rows="3"
                  value={specialRequests}
                  onChange={(e) => setSpecialRequests(e.target.value)}
                  placeholder="Any special instructions or requests..."
                />
              </div>
            </div>

            <div className="flex space-x-4">
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="flex-1 btn btn-primary"
              >
                {loading ? 'Booking...' : 'Confirm Booking'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold">Complete Payment</h2>
                <button
                  onClick={() => setShowPaymentModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded">
                <h3 className="font-semibold mb-2">Booking Summary</h3>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span>Service:</span>
                    <span className="font-medium">{selectedService?.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Date:</span>
                    <span className="font-medium">{selectedDate && format(selectedDate, 'MMMM d, yyyy')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Time:</span>
                    <span className="font-medium">{selectedSlot && formatTo12Hour(selectedSlot.startTime)}</span>
                  </div>
                  {selectedAddons.length > 0 && (
                    <div className="flex justify-between">
                      <span>Add-ons:</span>
                      <span className="font-medium">{selectedAddons.length} selected</span>
                    </div>
                  )}
                  <div className="flex justify-between text-lg font-bold pt-2 border-t border-blue-300 mt-2">
                    <span>Total:</span>
                    <span>${getTotalPrice()}</span>
                  </div>
                </div>
              </div>

              {/* Show Stripe card form if Stripe is selected and client secret is ready */}
              {stripePromise && stripeClientSecret ? (
                <Elements stripe={stripePromise}>
                  <StripeCheckoutForm
                    amount={parseFloat(getPaymentAmount())}
                    clientSecret={stripeClientSecret}
                    onSuccess={handleStripeSuccess}
                    onCancel={handleStripeCancel}
                  />
                </Elements>
              ) : showSquareForm && squareApplicationId && squareLocationId ? (
                <SquareCheckoutForm
                  amount={parseFloat(getPaymentAmount())}
                  applicationId={squareApplicationId}
                  locationId={squareLocationId}
                  onSuccess={handleSquareSuccess}
                  onCancel={handleSquareCancel}
                />
              ) : (
                <div className="space-y-3">
                  <div className="mb-4">
                    <p className="text-sm font-semibold text-gray-900 mb-1">
                      Click a payment method below to complete your booking:
                    </p>
                    <p className="text-xs text-gray-500">
                      {providers.some(p => p.provider === 'stripe' && p.is_active) 
                        ? 'Enter your card details securely' 
                        : 'You\'ll be redirected to complete payment securely'}
                    </p>
                  </div>

                  {providers.filter(p => p.provider !== 'cash_only' && p.is_active).length === 0 && (
                    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800">
                      No payment methods are currently configured. Please contact the administrator.
                    </div>
                  )}

                  {providers.filter(p => p.provider !== 'cash_only' && p.is_active).map(p => (
                  <button
                    key={p.provider}
                    onClick={() => handlePaymentMethodClick(p.provider)}
                    disabled={processingPayment}
                    className={`w-full p-5 border-2 rounded-lg transition-all text-left group ${
                      processingPayment && selectedPaymentMethod === p.provider
                        ? 'border-blue-500 bg-blue-50 shadow-lg'
                        : 'border-gray-300 hover:border-blue-500 hover:bg-blue-50 hover:shadow-md'
                    } ${processingPayment ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="font-bold text-lg capitalize flex items-center space-x-2 mb-1">
                          <span>{p.provider}</span>
                          {processingPayment && selectedPaymentMethod === p.provider && (
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                          )}
                        </div>
                        <div className="text-sm text-gray-600 mb-2">
                          {p.provider === 'stripe' && 'Pay with credit/debit card'}
                          {p.provider === 'paypal' && 'Pay with PayPal balance or card'}
                          {p.provider === 'square' && 'Pay with Square'}
                        </div>
                        {!processingPayment && (
                          <div className="text-xs text-blue-600 font-medium group-hover:underline">
                            → Click to pay ${getTotalPrice()} with {p.provider}
                          </div>
                        )}
                        {processingPayment && selectedPaymentMethod === p.provider && (
                          <div className="text-xs text-blue-600 font-medium">
                            Processing payment...
                          </div>
                        )}
                      </div>
                      <div className="text-2xl font-bold text-blue-600">
                        ${getTotalPrice()}
                      </div>
                    </div>
                  </button>
                ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookAppointment;
